/**
 * lazyLoad - Lightweight lazy-loader that replaces the `lazysizes` library for this
 * project's `data-bg` usage.
 *
 *  - Observes every `.lazyload` element and unveils it once it approaches the viewport.
 *  - Fires a bubbling, cancelable `lazybeforeunveil` event *before* applying the
 *    background, so the per-section inline `<script>` handlers in the AEM markup
 *    (which inject responsive `<style>` blocks) keep working.
 *  - Applies a deferred `data-bg` background image on unveil (e.g. the
 *    `.background.mobile-view` sections and the React `Image` component).
 *  - Toggles the `lazyloading` / `lazyloaded` classes and fires a `lazyloaded` event.
 *  - Re-processes elements whose `data-bg` changes after they were already unveiled —
 *    the `ls.attrchange` behaviour required by the React `Image` component, which
 *    mutates `data-bg` across re-renders.
 *  - Picks up `.lazyload` nodes added to the DOM dynamically (React renders, AJAX, etc.).
 *
 * The runtime is started via {@link initLazyLoad} from `app.js`.
 */

interface LazyLoadConfig {
  /** Class that marks an element as lazy. Default: `lazyload`. */
  lazyClass: string;
  /** Class added while the element is being unveiled. Default: `lazyloading`. */
  loadingClass: string;
  /** Class added once the element has been unveiled. Default: `lazyloaded`. */
  loadedClass: string;
  /** Attribute holding a deferred background image URL. Default: `data-bg`. */
  bgAttr: string;
  /**
   * Distance (in px) around the viewport within which elements are pre-loaded.
   * Mirrors the lazysizes `expand` option. Computed from the viewport at init when `null`.
   */
  expand: number | null;
}

const cfg: LazyLoadConfig = {
  lazyClass: "lazyload",
  loadingClass: "lazyloading",
  loadedClass: "lazyloaded",
  bgAttr: "data-bg",
  expand: null,
};

/** Tracks elements currently under observation to avoid duplicate observers. */
let observed = new WeakSet<HTMLElement>();

let intersectionObserver: IntersectionObserver | null = null;
let mutationObserver: MutationObserver | null = null;
let initialised = false;

/**
 * Dispatch a bubbling, cancelable custom event on an element.
 * @returns the dispatched event (inspect `defaultPrevented`).
 */
const fireEvent = (elem: HTMLElement, name: string): CustomEvent => {
  const event = new CustomEvent(name, {
    bubbles: true,
    cancelable: true,
  });
  elem.dispatchEvent(event);
  return event;
};

/** Finalise an element: swap loading -> loaded classes and fire `lazyloaded`. */
const markAsLoaded = (elem: HTMLElement): void => {
  elem.classList.remove(cfg.loadingClass);
  elem.classList.add(cfg.loadedClass);
  fireEvent(elem, "lazyloaded");
};

/**
 * Unveil a single lazy element: fire `lazybeforeunveil`, apply the deferred
 * background image and resolve the load lifecycle. Safe to call multiple times.
 *
 * @returns `true` when unveiling started (the element left the `lazyload` state),
 *   `false` when it was skipped (already handled or a listener called
 *   `preventDefault()`), so callers know whether to stop observing it.
 */
const unveil = (elem: HTMLElement): boolean => {
  if (
    elem.classList.contains(cfg.loadedClass) ||
    elem.classList.contains(cfg.loadingClass)
  ) {
    return false;
  }

  // Fire before touching the background, allowing listeners to cancel or mutate.
  const beforeEvent = fireEvent(elem, "lazybeforeunveil");
  if (beforeEvent.defaultPrevented) {
    return false;
  }

  elem.classList.add(cfg.loadingClass);

  // Apply a deferred background image. This makes the module self-contained
  // (it no longer depends on an external `lazybeforeunveil` listener in app.js)
  // and is what drives `.background.mobile-view` and other `data-bg` elements.
  const bg = elem.getAttribute(cfg.bgAttr);
  if (bg) {
    elem.style.backgroundImage = "url('" + bg + "')";
  }

  elem.classList.remove(cfg.lazyClass);

  // The `lazybeforeunveil` listeners and the background assignment have done their
  // work, so resolve on the next frame.
  requestAnimationFrame(() => markAsLoaded(elem));

  return true;
};

/** Begin observing an element for viewport proximity (idempotent). */
const observe = (elem: HTMLElement): void => {
  if (observed.has(elem)) return;
  observed.add(elem);

  if (intersectionObserver) {
    intersectionObserver.observe(elem);
  } else {
    // No IntersectionObserver support: unveil immediately.
    unveil(elem);
  }
};

/**
 * Reset an already-processed element back to the lazy state and re-observe it,
 * so a fresh `data-bg` value is picked up. This is the `attrchange` behaviour.
 */
const resetAndReobserve = (elem: HTMLElement): void => {
  elem.classList.remove(cfg.loadedClass, cfg.loadingClass);
  elem.classList.add(cfg.lazyClass);

  observed.delete(elem);
  if (intersectionObserver) {
    intersectionObserver.unobserve(elem);
  }
  observe(elem);
};

/** React to a `data-bg` mutation on a previously-seen element. */
const handleAttrChange = (elem: HTMLElement): void => {
  if (
    elem.classList.contains(cfg.loadedClass) ||
    elem.classList.contains(cfg.loadingClass)
  ) {
    // Already unveiled -> re-run so the new background is applied.
    resetAndReobserve(elem);
  } else if (elem.classList.contains(cfg.lazyClass)) {
    // Not yet unveiled -> make sure it is being watched.
    observe(elem);
  }
};

/** (Re)scan the document and observe any `.lazyload` element not yet tracked. */
const checkElems = (): void => {
  document
    .querySelectorAll<HTMLElement>("." + cfg.lazyClass)
    .forEach((elem) => observe(elem));
};

/**
 * Build the IntersectionObserver whose root margin pre-loads content approaching
 * the viewport, matching the lazysizes `expand` semantics.
 */
const createIntersectionObserver = (): IntersectionObserver | null => {
  if (typeof IntersectionObserver === "undefined") {
    return null;
  }

  const expand =
    cfg.expand != null
      ? cfg.expand
      : Math.max(
          300,
          Math.round(
            Math.max(
              window.innerHeight || 0,
              window.innerWidth || 0,
            ) * 0.5,
          ),
        );

  return new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const elem = entry.target as HTMLElement;
          // Only stop observing once unveiling actually started. A
          // `lazybeforeunveil` listener may `preventDefault()` to defer loading;
          // keeping the element observed lets a later intersection retry it.
          if (unveil(elem)) {
            obs.unobserve(elem);
            observed.delete(elem);
          }
        }
      });
    },
    { rootMargin: expand + "px" },
  );
};

/**
 * Watch the DOM for:
 *  - newly added `.lazyload` elements (childList), and
 *  - `data-bg` mutations on already-known elements (attrchange behaviour).
 */
const createMutationObserver = (): MutationObserver | null => {
  if (typeof MutationObserver === "undefined") {
    return null;
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "attributes") {
        handleAttrChange(mutation.target as HTMLElement);
        return;
      }

      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const elem = node as HTMLElement;
        if (elem.classList.contains(cfg.lazyClass)) {
          observe(elem);
        }
        elem
          .querySelectorAll<HTMLElement>("." + cfg.lazyClass)
          .forEach((child) => observe(child));
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [cfg.bgAttr],
  });

  return observer;
};

/**
 * Start the lazy-loading runtime.
 * Automatically defers the initial scan until the DOM is ready.
 */
export const initLazyLoad = (): void => {
  if (initialised || typeof window === "undefined") {
    return;
  }
  initialised = true;

  intersectionObserver = createIntersectionObserver();
  mutationObserver = createMutationObserver();

  const start = () => checkElems();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
};

/** Stop observers and clear module state so the loader can be initialized again. */
export const resetLazyLoad = (): void => {
  intersectionObserver?.disconnect();
  mutationObserver?.disconnect();
  intersectionObserver = null;
  mutationObserver = null;
  observed = new WeakSet<HTMLElement>();
  initialised = false;
};
